import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    image = "image",
    linkTitle = "linkTitle",
    linkURL = "linkURL",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.image]: string;
        [fields.linkTitle]: string;
        [fields.linkURL]: LinkFieldValue;
    };
}

export interface HomepageLookingExpertStyles {
    container?: InjectableCss;
    imgwrapper?: InjectableCss;
    descriptionWrapper?: InjectableCss;
    title?: InjectableCss;
    description?: InjectableCss;
    link?: InjectableCss;
}

export const homepageLookingExpertStyles: HomepageLookingExpertStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            padding-bottom: 104px;
            @media (min-width: 1801px) {
                padding-left: 20%;
                padding-right: 20%;
            }
            @media (min-width: 1401px) and (max-width: 1800px) {
                padding-left: 18%;
                padding-right: 18%;
            }
            @media (min-width: 992px) and (max-width: 1400px) {
                padding-left: 120px;
                padding-right: 120px;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        padding-bottom: 56px;
                    `,
                    css`
                        flex-direction: column;
                        padding-bottom: 56px;
                    `,
                    css`
                        padding-left: 25px;
                        padding-right: 25px;
                        padding-bottom: 78px;
                    `,
                    null,
                ]);
            }}
        `,
    },
    imgwrapper: {
        css: css`
            flex-basis: 35%;
            @media (min-width: 1433px) and (max-width: 1500px) {
                width: 30%;
                flex-basis: 30%;
            }
            @media (min-width: 992px) and (max-width: 1159px) {
                width: 45%;
                flex-basis: 45%;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        flex-basis: 100%;
                    `,
                    css`
                        width: 100%;
                        flex-basis: 100%;
                    `,
                    css`
                        width: 42%;
                        flex-basis: 42%;
                    `,
                    null,
                ]);
            }}
        `,
    },
    descriptionWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-basis: 65%;
            border: 1px solid #8f0446;
            padding: 56px;
            @media (min-width: 1433px) and (max-width: 1500px) {
                width: 70%;
                flex-basis: 70%;
            }
            @media (min-width: 992px) and (max-width: 1159px) {
                width: 55%;
                flex-basis: 55%;
                padding: 40px;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 32px;
                        flex-basis: 100%;
                        width: 100%;
                    `,
                    css`
                        padding: 32px;
                        flex-basis: 100%;
                        width: 100%;
                    `,
                    css`
                        flex-basis: 58%;
                        width: 58%;
                        padding: 25px;
                    `,
                    null,
                ]);
            }}
        `,
    },
    title: {
        css: css`
            color: ${getProp("theme.typography.h3.color")};
            margin-bottom: 24px !important;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 16px !important;
                    `,
                    css`
                        margin-bottom: 16px !important;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    description: {
        css: css`
            font-weight: 400;
            font-size: 18px;
            line-height: 24px;
            color: ${getProp("theme.typography.p.color")};
            margin-bottom: 24px !important;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 14px;
                        line-height: 20px;
                        margin-bottom: 16px !important;
                    `,
                    css`
                        font-size: 14px;
                        line-height: 20px;
                        margin-bottom: 16px !important;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    link: {
        css: css`
            height: 48px;
            background: #8f0446;
            border-radius: 24px;
            padding: 12px 24px;
            font-weight: 600;
            font-size: 18px;
            line-height: 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        height: 32px;
                        border-radius: 16px;
                        padding: 8px 16px;
                        font-size: 14px;
                        line-height: 16px;
                    `,
                    css`
                        height: 32px;
                        border-radius: 16px;
                        padding: 8px 16px;
                        font-size: 14px;
                        line-height: 16px;
                    `,
                    null,
                    null,
                ]);
            }}

            > span {
                color: #fff !important;
            }
        `,
    },
};

const styles = homepageLookingExpertStyles;

export const CENLookingForExpert: FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.linkURL);
    return (
        <StyledWrapper {...styles.container}>
            <LazyImage src={fields.image} {...styles.imgwrapper} altText={fields.title} />
            <StyledWrapper {...styles.descriptionWrapper}>
                <Typography variant="h3" {...styles.title}>
                    {fields.title}
                </Typography>
                <Typography variant="p" {...styles.description}>
                    {fields.description}
                </Typography>
                <StyledWrapper>
                    <Link data-test-selector="looking-expert-link" href={url} {...styles.link}>
                        {fields.linkTitle}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENLookingForExpert,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.title,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.description,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.linkTitle,
                editorTemplate: "TextField",
                displayName: "Link title",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.linkURL,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                displayName: "Link URL",
            },
            {
                name: fields.image,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
